/**
 * Appearance settings — theme mode, font size, accent color.
 *
 * Preferences live in localStorage (per-browser) and apply via CSS variables
 * + `<html class="dark">`. No backend round-trip; changes take effect
 * immediately and survive reload.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Palette, Sun, Moon, Laptop, Check, Type, Languages } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  ACCENT_PRESETS,
  applyAppearancePrefs,
  loadAppearancePrefs,
  saveAppearancePrefs,
  watchSystemTheme,
  type AppearancePrefs,
  type FontSize,
  type ThemeMode,
} from '@/lib/preferences';
import {
  LANGUAGE_LABELS,
  SUPPORTED_LANGUAGES,
  setLanguage,
  type SupportedLanguage,
} from '@/lib/i18n';

// Label keys (resolved via t() at render time so they re-translate when
// the user switches language).
const MODE_OPTIONS: { value: ThemeMode; labelKey: string; icon: typeof Sun }[] = [
  { value: 'light', labelKey: 'appearance.theme_light', icon: Sun },
  { value: 'dark', labelKey: 'appearance.theme_dark', icon: Moon },
  { value: 'system', labelKey: 'appearance.theme_system', icon: Laptop },
];

const FONT_OPTIONS: { value: FontSize; labelKey: string; preview: string }[] = [
  { value: 'small', labelKey: 'appearance.text_small', preview: 'Aa' },
  { value: 'normal', labelKey: 'appearance.text_normal', preview: 'Aa' },
  { value: 'large', labelKey: 'appearance.text_large', preview: 'Aa' },
];

export function AppearancePanel() {
  const { t, i18n } = useTranslation();
  const [prefs, setPrefs] = useState<AppearancePrefs>(() => loadAppearancePrefs());

  // Apply + persist whenever any field changes.
  useEffect(() => {
    applyAppearancePrefs(prefs);
    saveAppearancePrefs(prefs);
  }, [prefs]);

  // Listen for OS-level theme changes while user is on 'system' mode.
  useEffect(() => watchSystemTheme(() => prefs.mode), [prefs.mode]);

  const update = (partial: Partial<AppearancePrefs>) =>
    setPrefs((p) => ({ ...p, ...partial }));

  // Language change goes through i18next, not the appearance prefs object —
  // it owns its own localStorage key (`kidzio.locale`) used at boot.
  const currentLang = (i18n.resolvedLanguage ?? 'en') as SupportedLanguage;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Palette className="size-4" />
          {t('appearance.title')}
        </CardTitle>
        <CardDescription>
          {t('appearance.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Theme mode */}
        <div>
          <p className="mb-2 text-sm font-medium">{t('appearance.theme')}</p>
          <div className="grid grid-cols-3 gap-2">
            {MODE_OPTIONS.map(({ value, labelKey, icon: Icon }) => {
              const active = prefs.mode === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => update({ mode: value })}
                  className={cn(
                    'flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors',
                    active
                      ? 'border-[color:var(--kz-color-brand-primary)] bg-[color:var(--kz-color-brand-primary)]/10 text-[color:var(--kz-color-brand-primary)]'
                      : 'border-[color:var(--kz-color-border-subtle)] text-[color:var(--kz-color-ink-secondary)] hover:bg-[color:var(--kz-color-surface-sunken)]',
                  )}
                  aria-pressed={active}
                >
                  <Icon className="size-4" />
                  {t(labelKey)}
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-xs text-[color:var(--kz-color-ink-muted)]">
            {t('appearance.theme_system_hint')}
          </p>
        </div>

        {/* Font size */}
        <div>
          <p className="mb-2 flex items-center gap-1.5 text-sm font-medium">
            <Type className="size-3.5" />
            {t('appearance.text_size')}
          </p>
          <div className="grid grid-cols-3 gap-2">
            {FONT_OPTIONS.map(({ value, labelKey, preview }) => {
              const active = prefs.font_size === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => update({ font_size: value })}
                  className={cn(
                    'rounded-xl border px-3 py-2.5 text-center transition-colors',
                    active
                      ? 'border-[color:var(--kz-color-brand-primary)] bg-[color:var(--kz-color-brand-primary)]/10 text-[color:var(--kz-color-brand-primary)]'
                      : 'border-[color:var(--kz-color-border-subtle)] hover:bg-[color:var(--kz-color-surface-sunken)]',
                  )}
                  aria-pressed={active}
                >
                  <span
                    className={cn(
                      'font-bold',
                      value === 'small' && 'text-sm',
                      value === 'normal' && 'text-base',
                      value === 'large' && 'text-lg',
                    )}
                  >
                    {preview}
                  </span>
                  <p className="mt-0.5 text-[11px] font-medium">{t(labelKey)}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Language */}
        <div>
          <p className="mb-2 flex items-center gap-1.5 text-sm font-medium">
            <Languages className="size-3.5" />
            {t('common.language')}
          </p>
          <div className="flex flex-wrap gap-2">
            {SUPPORTED_LANGUAGES.map((code) => {
              const info = LANGUAGE_LABELS[code];
              const active = currentLang === code;
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => void setLanguage(code)}
                  aria-pressed={active}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors',
                    active
                      ? 'border-[color:var(--kz-color-brand-primary)] bg-[color:var(--kz-color-brand-primary)]/10 text-[color:var(--kz-color-brand-primary)] font-medium'
                      : 'border-[color:var(--kz-color-border-subtle)] text-[color:var(--kz-color-ink-secondary)] hover:bg-[color:var(--kz-color-surface-sunken)]',
                  )}
                >
                  <span aria-hidden>{info.flag}</span>
                  {info.native}
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-xs text-[color:var(--kz-color-ink-muted)]">
            {t('appearance.language_hint')}
          </p>
        </div>

        {/* Accent color */}
        <div>
          <p className="mb-2 text-sm font-medium">{t('appearance.accent')}</p>
          <div className="flex flex-wrap gap-2">
            {ACCENT_PRESETS.map((preset) => {
              const active = prefs.accent_id === preset.id;
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => update({ accent_id: preset.id })}
                  aria-label={preset.label}
                  aria-pressed={active}
                  className={cn(
                    'relative grid size-10 place-items-center rounded-full transition-all',
                    active
                      ? 'ring-2 ring-offset-2 ring-[color:var(--kz-color-brand-primary)]'
                      : 'hover:scale-110',
                  )}
                  style={{
                    background: `linear-gradient(135deg, ${preset.primary}, ${preset.secondary})`,
                  }}
                  title={preset.label}
                >
                  {active && <Check className="size-4 text-white drop-shadow" />}
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-xs text-[color:var(--kz-color-ink-muted)]">
            {t('appearance.accent_hint')}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
